<template>
  <div class="assertion-list">
    <div class="assertion-list-header">
      <div class="text-end">date</div>
      <div></div>
      <div></div>
      <div>type</div>
      <div>details</div>
    </div>
    <ul class="list-unstyled assertion-list-area">
      <li v-for="set in assertionSetsRef" :key="set[0].id">
        <div v-if="showTimeGap(set[0].id!) || showDistance(set[0].id!)" class="assertion-diff">
          <div v-if="showTimeGap(set[0].id!)" class="assertion-time-gap">
            <div>{{ getTimeGapText(set[0].id!) }}</div>
          </div>
          <div v-if="showTimeGap(set[0].id!) || showDistance(set[0].id!)" class="time-ellipsis">
            <div>&#8942;</div>
          </div>
          <div v-if="showDistance(set[0].id!)" class="assertion-distance">
            <div><div class="place-ellipsis">&#8942;</div><span class="distance-text">{{ getDistanceText(set[0].id!) }}</span></div>
          </div>
        </div>
        <AssertionSet 
          :assertionIds="set.map(a => a.id!)" 
          :key="set[0].id"
          :base-date="birthDateRef"
          :dimmed-ids="getDimmedIds(set)"
          :show-count="showCounts"
          @click="onAssertionClick"/>
      </li>
      <li v-if="allowEditRef">
        <div class="add-row">
          <button type="button" class="btn btn-inline btn-link" @click="onAdd">
            Add detail
          </button>
        </div>
      </li>
      <li v-for="i in fillerRowCountRef" :key="i" class="filler-row"></li>
    </ul>
    <AssertionModal ref="assertionModalRef" :context-person-id="personId" @delete="confirmDelete"/>
    <Modal ref="confirmDeleteModalRef" class="confirm-delete" ok-style="danger" @ok="onDelete">
      <template #title>Delete detail</template>
      <template #default="{ state }">
        <div>
          Are you sure you want to delete this detail?
        </div>
        <div class="assertion-type">
          {{ state?.assertionType }}
        </div>
      </template>
      <template #okText>
        <Trash2Icon/>
        Delete
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.assertion-list {
  .assertion-list-header {
    border-bottom: 1px solid rgb(254, 203, 209); // pink
  }

  .assertion-diff, .add-row, .filler-row {
    border-bottom: 1px solid rgb(237, 239, 240); // pale blue-gray
  }
}

.assertion-list-header,
.assertion-diff,
.add-row {
  // same grid columns as AssertionRow.assertion-fields
  display: grid;
  grid-template-columns: 18fr 5fr 20px 19fr 50fr;
  column-gap: 0.5rem;
  padding: 0 0.5rem;
  padding-bottom: 2px;
}

.assertion-list-header {
  padding: 1.5rem 0.5rem 0;
  font-size: 0.75rem;
  color: #888;

  .actions-spacer {
    width: 1rem;
  }
}

.assertion-list-area {
  margin-bottom: 1.25rem;
}

.assertion-diff {
  padding-bottom: 0.05rem;
  margin-bottom: 0.05rem;
  font-size: 0.75rem;
  color: #888;
}

.time-ellipsis {
  grid-column: 2;
  text-align: center;
}
.assertion-time-gap {
  grid-column: 1;
  text-align: end;
  font-style: italic;
}
.place-ellipsis {
  display: inline-block;
  width: 1.5rem;
  padding-left: 0.4rem;
}
.assertion-distance {
  grid-column: 5;
  text-align: start;
}
.distance-text {
  font-style: italic;
}

.add-row {
  button {
    grid-column: 4;
    font-size: 0.875rem;
  }
}

.filler-row {
  height: 1.5rem;
}

.confirm-delete :deep(.modal-body) {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  .assertion-type {
    margin-left: 1rem;
  }
}
</style>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Assertion, AssertionType } from '@/rd/ResearchDataModel'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { usePersonStore } from '@/rd/PersonStore'
import { useFamilyStore } from '@/rd/FamilyStore'
import { useAssertionStore } from '@/rd/AssertionStore'
import { usePlaceStore } from '@/rd/PlaceStore'
import { isDefined } from '@/util/TypeScriptUtil'
import { GeoUtil } from '@/util/GeoUtil'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { ItemPermissions } from '@/gp/GroupAdminModel'
import { CompositeId } from '@/rd/CompositeId'
import { LoadMode } from '@/util/AsyncData'
import AssertionSet from '@/explore/AssertionSet.vue'
import AssertionModal from '@/manage/AssertionModal.vue'
import Modal from '@/util/Modal.vue'
import { AsyncUtil } from '@/util/AsyncUtil'
import { hideModalAsync } from '@/util/AppUtil'
import { Trash2Icon } from '@zhuowenli/vue-feather-icons'

const props = defineProps<{
  personId?: string
  includeRelated?: boolean
  includeRestricted?: boolean
  ownerOnly?: boolean
  dimOtherOwners?: boolean
  showCounts?: boolean
}>()

const assertionModalRef = ref<InstanceType<typeof AssertionModal>>()
const confirmDeleteModalRef = ref<InstanceType<typeof Modal>>()

const dataGroupStore = useDataGroupStore()
const viewPersonStore = useViewPersonStore()
const personStore = usePersonStore()
const familyStore = useFamilyStore()
const assertionStore = useAssertionStore()
const placeStore = usePlaceStore()

const orderedTypes: string[] = [
  AssertionType.Living,
  AssertionType.Name,
  AssertionType.Gender,
  AssertionType.Child,
]

const groupIdRef = computed(() => CompositeId.getGroupId(props.personId))
const permissionsRef = computed(() =>  dataGroupStore.getAsyncPermissions(groupIdRef.value, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)
const personGroupRef = computed(() => dataGroupStore.getAsyncGroup(groupIdRef.value, LoadMode.EnsureLoaded)?.data)
const allowEditRef = computed(() => (personGroupRef.value?.isProfile && !personGroupRef.value?.trackedGroupId && permissionsRef.value & ItemPermissions.Modify) != 0)
const personRef = computed(() => personStore.getAsyncPerson(props.personId)?.data) // loaded by parent
const viewPersonRef = computed(() => viewPersonStore.getAsyncPerson(props.personId)?.data) // loaded by parent
const birthDateRef = computed(() => (props.ownerOnly ? personRef.value?.displayProperties : viewPersonRef.value?.displayProperties)?.birthDate.date1)
const spouseViewFamiliesRef = computed(() => {
  if (!props.personId || !viewPersonRef.value || !props.includeRelated)
    return []

  return viewPersonStore.getLoadedSpouseFamilies(viewPersonRef.value?.id).spouseFamilies
})
const assertionIdsRef = computed(() => {
  if (!props.personId || !viewPersonRef.value)
    return []

  const keyLists = personStore.getAsyncAssertionKeyLists(viewPersonRef.value.matchIds)

  if (props.includeRelated) {
    const spouseFamilyIds = spouseViewFamiliesRef.value.flatMap(vf => vf.matchIds)
    keyLists.push(...familyStore.getAsyncAssertionKeyLists(spouseFamilyIds))
  }
  return keyLists.flatMap(list => list?.data?.keys ?? [])
})
const assertionSetsRef = computed(() => {
  if (!props.personId || !viewPersonRef.value)
    return []

  const hiddenAssertionTypes = ["Child", "Living"]
  const assertions = assertionStore.getAsyncAssertionList(assertionIdsRef.value)
    .map(a => a.data).filter(isDefined)
    .filter(a => props.includeRestricted || !a.restricted)
    .filter(a => !props.ownerOnly || CompositeId.hasGroupId(a.id, groupIdRef.value!))
    .filter(a => !hiddenAssertionTypes.includes(a.assertionType))

  if (props.ownerOnly) {
    const assertionSets = assertions.map(a => [a])
    assertionSets.sort((a, b) => Assertion.compareDate(a[0], b[0]) || compareType(a[0], b[0]))
    return assertionSets
  }

  const viewPersonIdMap = new Map<string, string>()
  for (const matchId of viewPersonRef.value.matchIds) {
    viewPersonIdMap.set(matchId, props.personId) // *:1
  }

  const viewFamilyIdMap = new Map(spouseViewFamiliesRef.value.flatMap(vf => vf.matchIds.map(id => [id, vf.id!])))

  const assertionSets = buildViewAssertionSets(assertions, viewPersonIdMap, viewFamilyIdMap)
  for (const set of assertionSets) {
    set.sort(Assertion.comparePrecedence)
  }
  assertionSets.sort((a, b) => Assertion.compareDate(a[0], b[0]) || compareType(a[0], b[0]))
  
  return assertionSets
})

const fillerRowCountRef = computed(() => Math.max(6 - assertionSetsRef.value.length, 0))

function buildViewAssertionSets(assertions: Assertion[], viewPersonIdMap: Map<string, string>, viewFamilyIdMap: Map<string, string>) {
  const assertionSetMap = new Map<string, Assertion[]>()
  for (const a of assertions) {
    const subjectViewId = a.subjectType == "Person" 
      ? viewPersonIdMap.get(a.subjectId!)
      : viewFamilyIdMap.get(a.subjectId!)

    const key = `${a.subjectType}:${subjectViewId}:${a.assertionType}:${a.precedence?.instanceId ?? 1}`
    const set = assertionSetMap.get(key)
    if (set) {
      set.push(a)
    } else {
      assertionSetMap.set(key, [a])
    }
  }
  return Array.from(assertionSetMap.values())
}

const assertionTimeGapMap = computed(() => {
  const map = new Map<string, number>()
  let current = assertionSetsRef.value.at(0)
  for (let i = 1; i < assertionSetsRef.value.length; i++) {
    const prev = current!
    current = assertionSetsRef.value[i]

    if (!prev[0].sortDate.isEmpty && !current[0].sortDate.isEmpty) {
      // don't include gap between birth and first assertion since the gap is equal to the age, which is already shown
      if (birthDateRef.value && prev[0].sortDate.date1.diff(birthDateRef.value).years > 1) {
        const gap = current[0].sortDate.date1.diff(prev[0].sortDate.date1)
        map.set(current[0].id!, gap.years)
      }
    }
  }
  return map
})

const assertionDistanceMap = computed(() => {
  const map = new Map<string, number>()
  for (let i = 1; i < assertionSetsRef.value.length; i++) {
    const prev = assertionSetsRef.value[i - 1]
    const current = assertionSetsRef.value[i]

    if (prev[0].placeId && current[0].placeId) {
      const prevPlace = placeStore.trackAsyncPlace(prev[0].placeId)!.data
      const currentPlace = placeStore.trackAsyncPlace(current[0].placeId)!.data

      if (prevPlace && currentPlace) {
        const distance = prevPlace.distanceTo(currentPlace)
        if (distance) {
          map.set(current[0].id!, distance)
        }
      }
    }
  }
  return map
})

function showTimeGap(id: string) {
  const gap = assertionTimeGapMap.value.get(id)
  return gap && gap > 10 // years
}

function getTimeGapText(id: string) {
  const gap = assertionTimeGapMap.value.get(id)
  return `${Math.floor(gap ?? 0)} years`
}

function showDistance(id: string) {
  const distance = assertionDistanceMap.value.get(id)
  return distance && distance > 150 // km, or ~100 miles
}

function getDistanceText(id: string) {
  const distance = assertionDistanceMap.value.get(id)
  return `${Math.floor(GeoUtil.kmToMi(distance ?? 0)).toLocaleString()} miles`
}

function compareType(a:Assertion, b:Assertion) {
  const aIndex = orderedTypes.indexOf(a.assertionType)
  const bIndex = orderedTypes.indexOf(b.assertionType)
  return (aIndex == -1 ? 999 : aIndex) - (bIndex == -1 ? 999 : bIndex) // ordered types sort first
}

function getDimmedIds(set: Assertion[]) {
  if (!props.dimOtherOwners || !groupIdRef.value)
    return undefined

  return set.filter(a => !CompositeId.hasGroupId(a.id, groupIdRef.value!)).map(a => a.id!)
}

function onAssertionClick(id: string) {
  if (props.personId) {
    assertionModalRef.value?.open(id)
  }
}

function onAdd() {
  assertionModalRef.value?.open()
}

async function confirmDelete(assertionId?: string) {
  if (assertionId) {
    await hideModalAsync(assertionModalRef.value?.$el)
    const a = assertionStore.getAsyncAssertion(assertionId)?.data
    confirmDeleteModalRef.value!.open(a)
  }
}

async function onDelete(assertion?: Assertion) {
  if (assertion) {
    try {
      await AsyncUtil.atLeast(200, assertionStore.deleteAsync(assertion.id!))
      confirmDeleteModalRef.value?.close()
    }
    finally {
      confirmDeleteModalRef.value?.reset()
    }
  }
}
</script>
